import {
  Component,
  EventEmitter,
  Inject,
  Injector,
  Output,
  OnInit,
  AfterViewInit,
  Renderer2,
  Input,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TableFilter } from '../../interfaces';
import { RoutingService } from '../../helper';
import { ActivatedRoute } from '@angular/router';
import { IconsConstants } from '../../common';
import { of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { BaseConfirmationComponent } from '../base-confirmation-component/base-confirmation-component';
import { LocalStorageHelper } from '../../helper/local-storage-helper';
@Component({
  standalone: true,
  selector: 'app-base-filter-component',
  template: ``
})
export abstract class BaseFilterComponent<FilterChangeType> extends BaseConfirmationComponent implements OnInit, AfterViewInit {
  form!: FormGroup | FilterFormGroup;
  disableSearch: boolean = false;
  disableReset: boolean = false;
  isSearch: boolean;
  filtersCount: number;
  @Input() searchKey: string = 'search';
  @Output() filterChanged: EventEmitter<TableFilter<FilterChangeType>> = new EventEmitter<TableFilter<FilterChangeType>>();
  @Output() isExport: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('exportReportModal', { static: true }) exportReportModalComponent: TemplateRef<any>;
  
  // constants
  public IconsConstants = IconsConstants;
  private keydownEnterListener: () => void;
  private keydownEscListener: () => void;
  constructor(
    @Inject(String) protected override translationArr: string[] = [],
    protected override injector: Injector = null,
  ) {
    super([...translationArr, 'SUCCESS_MESSAGES', 'POPUPS'], injector);
  }

  ngOnInit(): void {
    this.initializeForm();
    this.getQueryParamsFromUrl();
    this.isSearch = this.checkAndCountNonNullFilters();
  }

  ngAfterViewInit(): void {
    const renderer = this.injector.get(Renderer2);
    const filterElements = document.querySelectorAll('.filter');
    if (filterElements) {
      filterElements.forEach((element) => {
        this.keydownEnterListener = renderer.listen(element, 'keydown.enter', () => this.onSearch());
        this.keydownEscListener = renderer.listen(element, 'keydown', (event: KeyboardEvent) => {
          if (event.key === 'Escape' || event.key === 'Esc') {
            this.onReset();
          }
        });
      });
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.keydownEnterListener || this.keydownEscListener) {
      this.keydownEnterListener();
      this.keydownEscListener();
    }
  }

  // publics
  onSearch(): void {
    const routingService = this.injector.get(RoutingService);
    routingService.setQueryParamsInUrl([{ key: this.searchKey, value: JSON.stringify(this.form.value) }]).then((_) => {
      this.filterChanged.emit({ filter: this.form.value, isSearch: true });
      this.disableSearch = true;
      this.disableReset = false;
      this.isSearch = this.checkAndCountNonNullFilters();
    });
  }

  onReset(resetValue?: object): void {
    const routingService = this.injector.get(RoutingService);
    routingService.setQueryParamsInUrl([{ key: this.searchKey, value: undefined }]).then((_) => {
      this.form.reset(resetValue);
      this.filterChanged.emit({ filter: this.form.value, isSearch: true });
      this.disableSearch = true;
      this.disableReset = true;
      this.isSearch = this.checkAndCountNonNullFilters();
    });
  }

  onExport(): void {
    this.onOpenConfirmation(
      this.injector.get(MatDialog),
      this.exportReportModalComponent,
      () => of(null),
      () => this.isExport.emit(true),
      this.translationsList['POPUPS']['CONFIRMATION_POPUP']['TITLE']['EXTRACT_REPORT'],
      this.translationsList['SUCCESS_MESSAGES']['DO_YOU_WANT_TO_TAKE_ACTION']
    )
  }

  //protected
  // this method for initialize filter form
  protected abstract initializeForm(): void;

  protected getQueryParamsFromUrl(): void {
    const routingService = this.injector.get(RoutingService);
    this.disableSearch = true;
    this.disableReset = false;
    const isThemeUpdated = LocalStorageHelper.getItem('IsThemeUpdated') === "true";
    const queries: string[] = routingService.getQueryParamsFromUrl(this.injector.get(ActivatedRoute), [this.searchKey]);
    if (queries[0]) {
      this.form.setValue(JSON.parse(queries[0]));
    }
    let routeQueryParams = this.injector.get(ActivatedRoute).snapshot.queryParams;
    this.form.patchValue(routeQueryParams);
    if(isThemeUpdated) {
      this.handleFormReset();
      routingService.removeQueryParamsFromUrl();
    }
    this.filterChanged.emit({ filter: this.form?.value, isSearch: false });
  }

  protected checkAndCountNonNullFilters(): boolean {
    this.filtersCount = this.form?.value ? Object.values(this.form.value).filter(value => value !== null && value !== "" && value !== undefined).length : 0;
    return this.filtersCount > 0;
  }

  private handleFormReset(): void {
    if (this.form instanceof FilterFormGroup){
      this.form.formSubmitted.emit('reset');
    } 
    this.form.reset();
  }
}

export class FilterFormGroup extends FormGroup {
  formSubmitted: EventEmitter<'search' | 'reset' | 'load'> = new EventEmitter();
}
